<template>
  <div class="media-page page-canales h-full w-full p-4 relative">
    <!-- Hero -->
    <!-- <section class="media-hero mb-5">
      <h2 class="text-xl font-bold mb-2">Canales y enlaces</h2>
      <p class="hero-subtitle">Canales con indicadores de conexi&oacute;n, tipo de plataforma y enlaces para compartir.</p>
    </section> -->

    <!-- Toolbar -->
    <section class="canales-toolbar mb-4">
      <div class="canales-toolbar-search">
        <i class="pi pi-search canales-search-icon"></i>
        <InputText
          v-model="searchQuery"
          placeholder="Buscar por nombre o link..."
          class="canales-filter-input"
        />
      </div>
      <div class="canales-toolbar-actions">
        <Button
          icon="pi pi-refresh"
          class="p-button-text p-button-sm p-button-secondary"
          :loading="loading"
          @click="loadBrands()"
          v-tooltip.left="'Recargar datos'"
        />
      </div>
    </section>

    <!-- Status filters -->
    <section class="canales-status-filters mb-4">
      <div class="canales-filter-group" role="group" aria-label="Filtrar por estado de conexi&oacute;n">
        <label
          v-for="opt in statusFilterOptions"
          :key="opt.value"
          class="canales-filter-chip"
          :class="{ 'is-active': statusFilter === opt.value }"
        >
          <input
            type="radio"
            name="statusFilter"
            :checked="statusFilter === opt.value"
            @change="statusFilter = opt.value"
          />
          <span v-if="opt.icon" :class="opt.icon" class="text-[11px]"></span>
          <span>{{ opt.label }}</span>
          <span class="canales-filter-count">{{ opt.count }}</span>
        </label>
      </div>
    </section>

    <!-- Type & Media filters -->
    <section class="canales-secondary-filters mb-4">
      <div class="canales-filter-group" role="group" aria-label="Filtrar por tipo de plataforma">
        <label
          v-for="opt in typeFilterOptions"
          :key="opt.value"
          class="canales-filter-chip"
          :class="{ 'is-active': typeFilter === opt.value }"
        >
          <input type="radio" name="typeFilter" :checked="typeFilter === opt.value" @change="typeFilter = opt.value" />
          <span v-if="opt.icon" :class="opt.icon" class="text-[11px]"></span>
          <span>{{ opt.label }}</span>
          <span class="canales-filter-count">{{ opt.count }}</span>
        </label>
      </div>
      <div class="canales-filter-separator"></div>
      <div class="canales-filter-group" role="group" aria-label="Filtrar por tipo de contenido">
        <label
          v-for="opt in mediaFilterOptions"
          :key="opt.value"
          class="canales-filter-chip"
          :class="{ 'is-active': mediaFilter === opt.value }"
        >
          <input type="radio" name="mediaFilter" :checked="mediaFilter === opt.value" @change="mediaFilter = opt.value" />
          <span v-if="opt.icon" :class="opt.icon" class="text-[11px]"></span>
          <span>{{ opt.label }}</span>
          <span class="canales-filter-count">{{ opt.count }}</span>
        </label>
      </div>
    </section>

    <!-- Error banner -->
    <div
      v-if="error"
      class="mb-4 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm flex items-center gap-2"
    >
      <i class="pi pi-exclamation-triangle"></i>
      <span>{{ error }}</span>
      <Button
        icon="pi pi-times"
        class="p-button-text p-button-sm p-button-secondary ml-auto"
        @click="error = ''"
      />
    </div>

    <div class="canales-table-wrapper relative">
      <!-- DataTable -->
      <section class="media-table-wrap flex-1">
        <DataTable
          ref="canalesTable"
          :value="filteredBrands"
          :loading="loading"
          responsiveLayout="scroll"
          stripedRows
          class="text-sm canales-table"
          :sortField="tableSortField"
          :sortOrder="tableSortOrder"
          @sort="onSort"
          :emptyMessage="searchQuery ? 'No se encontraron canales con ese criterio' : 'No hay canales disponibles'"
        >
          <Column header="Logo" style="width:90px">
            <template #body="{ data }">
              <div class="flex items-center justify-center">
                <div
                  class="w-14 h-14 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0"
                  :style="logoBackgroundStyle(data)"
                >
                  <img
                    v-if="getLogoUrl(data)"
                    :src="getLogoUrl(data)"
                    :alt="data.name || ''"
                    class="max-w-full max-h-full object-contain"
                    draggable="false"
                  />
                  <i v-else class="pi pi-megaphone text-white" style="font-size:1.3rem; opacity:0.55"></i>
                </div>
              </div>
            </template>
          </Column>

          <Column field="name" header="Canal" style="width:260px">
            <template #body="{ data }">
              <div class="flex items-center gap-2 leading-tight">
                                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="font-semibold text-zinc-700 dark:text-zinc-200 truncate">{{ data.name || '' }}</span>
                  <a
                    :href="getBrandLink(data)"
                    target="_blank"
                    class="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate inline-flex items-center gap-1"
                  >
                    {{ data.link || '' }}
                    <i class="pi pi-external-link text-[10px]"></i>
                  </a>
                </div>
              </div>
            </template>
          </Column>

          <Column
            field="_sortConectados"
            header="Con"
            :sortable="true"
            style="width:70px"
          >
            <template #body="{ data }">
              <div class="canales-stat-col">
                <span class="canales-stat-green" v-tooltip.top="'Conectados'">
                  <span class="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                  <span>{{ data.conectados || 0 }}</span>
                </span>
              </div>
            </template>
          </Column>

          <Column
            field="_sortVolumenBajo"
            header="Vol"
            :sortable="true"
            style="width:65px"
          >
            <template #body="{ data }">
              <div class="canales-stat-col">
                <span class="canales-stat-orange" v-tooltip.top="'Volumen bajo'" :class="{ 'is-zero': !data.volumen_bajo }">
                  <i class="pi pi-volume-off text-[11px]"></i>
                  <span>{{ data.volumen_bajo || 0 }}</span>
                </span>
              </div>
            </template>
          </Column>

          <Column
            field="_sortPausado"
            header="Pausa"
            :sortable="true"
            style="width:65px"
          >
            <template #body="{ data }">
              <div class="canales-stat-col">
                <span class="canales-stat-red" v-tooltip.top="'Pausados'" :class="{ 'is-zero': !data.pausado }">
                  <i class="pi pi-pause text-[11px]"></i>
                  <span>{{ data.pausado || 0 }}</span>
                </span>
              </div>
            </template>
          </Column>

          <Column header="Tipo" style="width:90px">
            <template #body="{ data }">
              <div class="canales-stat-col">
                <span v-if="data._isSonara" v-tooltip.top="'Sonara'" class="flex">
                  <svg
                    width="22" height="22" viewBox="0 0 22 22"
                    class="canales-type-svg flex-shrink-0"
                  >
                    <circle cx="11" cy="11" r="10" fill="#ff0000" stroke="#fff" stroke-width="2"/>
                    <circle cx="11" cy="11" r="5" fill="#000" stroke="#fff" stroke-width="2"/>
                  </svg>
                </span>
                <img
                  v-else
                  v-tooltip.top="'Vybroo'"
                  src="/img/vybroo-icon.png"
                  alt="Vybroo"
                  width="22" height="22"
                  class="canales-type-svg flex-shrink-0 rounded-full"
                />
              </div>
            </template>
          </Column>
          <Column header="Media" style="width:90px">
            <template #body="{ data }">
              <div class="canales-stat-col">
                <span class="canales-badge-type" :class="data._isVideo ? 'badge-video' : 'badge-musica'">
                  <i v-if="data._isVideo" class="pi pi-video text-[10px]"></i>
                  <i v-else class="pi pi-volume-up text-[10px]"></i>
                  <span v-if="data._isVideo">Video</span>
                  <span v-else>M&uacute;sica</span>
                </span>
              </div>
            </template>
          </Column>

          <Column header="Acciones" style="width:100px">
            <template #body="{ data }">
              <div class="flex gap-0.5">
                <Button
                  icon="pi pi-send"
                  class="p-button-text p-button-sm p-button-secondary"
                  v-tooltip.top="'Enviar por WhatsApp'"
                  @click="whatsAppBrand(data)"
                />
                <Button
                  icon="pi pi-copy"
                  class="p-button-text p-button-sm p-button-secondary"
                  v-tooltip.top="'Copiar enlace'"
                  @click="copyLink(getBrandLink(data))"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </section>

      <!-- A-Z Scroll Index -->
      <nav
        v-if="!loading && filteredBrands.length > 0"
        class="canales-alpha-nav"
        aria-label="Navegaci&oacute;n alfab&eacute;tica"
      >
        <button
          v-for="letter in alphaLetters"
          :key="letter"
          class="canales-alpha-btn"
          :class="{
            'is-available': availableLetters.has(letter),
            'is-empty': !availableLetters.has(letter)
          }"
          :disabled="!availableLetters.has(letter)"
          :title="availableLetters.has(letter) ? 'Ir a letra ' + letter : 'Sin canales con ' + letter"
          @click="scrollToLetter(letter)"
        >
          {{ letter }}
        </button>
      </nav>
    </div>

    <transition name="canales-top-fab">
      <button
        v-if="showBackToTop"
        type="button"
        class="canales-top-fab"
        @click="scrollToTop"
        aria-label="Volver arriba"
        title="Ir arriba"
      >
        <i class="pi pi-arrow-up"></i>
      </button>
    </transition>

    <Toast group="canales" position="bottom-right" />
  </div>
</template>

<script>
import axiosInstance from '@/axiosInstance';

export default {
  name: 'CanalesEnlaces',

  emits: ['update-loading'],

  data() {
    return {
      brands: [],
      searchQuery: '',
      statusFilter: 'all',
      typeFilter: 'all',
      mediaFilter: 'all',
      loading: false,
      error: '',
      tableSortField: null,
      tableSortOrder: null,
      showBackToTop: false,
      backToTopScrollTarget: null,
    };
  },

  computed: {
    brandsWithLetter() {
      return this.brands.map((b) => ({
        ...b,
        _letter: b.name
          ? b.name.trim().charAt(0).toUpperCase()
          : '#',
        _sortConectados: Number(b.conectados) || 0,
        _sortVolumenBajo: Number(b.volumen_bajo) || 0,
        _sortPausado: Number(b.pausado) || 0,
        _isSonara: !!(b.sonara || (b.link && String(b.link).toLowerCase().includes('sonara'))),
        _isVideo: !!(b.is_video || b.isVideo || b.video || b.video_enabled || b.videoEnabled || b.hasVideo || b.has_video || b.tiene_video || b.id_video_youtube || (b.stream && typeof b.stream === 'string' && b.stream.toLowerCase().includes('video')) || (b.link && typeof b.link === 'string' && b.link.toLowerCase().includes('video')) || (b.name && typeof b.name === 'string' && b.name.toLowerCase().includes('video'))),
      }));
    },

    statusFilterOptions() {
      const all = this.brands;
      const conectados = all.filter((b) => (b.conectados || 0) > 0);
      const desconectados = all.filter((b) => !b.conectados || b.conectados === 0);
      const volumenBajo = all.filter((b) => (b.volumen_bajo || 0) > 0);
      const pausados = all.filter((b) => (b.pausado || 0) > 0);
      return [
        { value: 'all', label: 'Todos', count: all.length, icon: '' },
        { value: 'conectados', label: 'Conectados', count: conectados.length, icon: 'pi pi-check-circle' },
        { value: 'desconectados', label: 'Desconectados', count: desconectados.length, icon: 'pi pi-minus-circle' },
        { value: 'volumen_bajo', label: 'Vol. bajo', count: volumenBajo.length, icon: 'pi pi-volume-off' },
        { value: 'pausados', label: 'Pausados', count: pausados.length, icon: 'pi pi-pause' },
      ];
    },

    typeFilterOptions() {
      const all = this.brands;
      const sonara = all.filter((b) => {
        return !!(b.sonara || (b.link && String(b.link).toLowerCase().includes('sonara')));
      });
      const vybroo = all.filter((b) => {
        return !(b.sonara || (b.link && String(b.link).toLowerCase().includes('sonara')));
      });
      return [
        { value: 'all', label: 'Todos', count: all.length, icon: '' },
        { value: 'sonara', label: 'Sonara', count: sonara.length, icon: 'pi pi-globe' },
        { value: 'vybroo', label: 'Vybroo', count: vybroo.length, icon: 'pi pi-building' },
      ];
    },

    mediaFilterOptions() {
      const all = this.brands;
      const video = all.filter((b) => {
        return !!(b.is_video || b.isVideo || b.video || b.video_enabled || b.videoEnabled || b.hasVideo || b.has_video || b.tiene_video || b.id_video_youtube || (b.stream && typeof b.stream === 'string' && b.stream.toLowerCase().includes('video')) || (b.link && typeof b.link === 'string' && b.link.toLowerCase().includes('video')) || (b.name && typeof b.name === 'string' && b.name.toLowerCase().includes('video')));
      });
      const musica = all.filter((b) => {
        return !(b.is_video || b.isVideo || b.video || b.video_enabled || b.videoEnabled || b.hasVideo || b.has_video || b.tiene_video || b.id_video_youtube || (b.stream && typeof b.stream === 'string' && b.stream.toLowerCase().includes('video')) || (b.link && typeof b.link === 'string' && b.link.toLowerCase().includes('video')) || (b.name && typeof b.name === 'string' && b.name.toLowerCase().includes('video')));
      });
      return [
        { value: 'all', label: 'Todos', count: all.length, icon: '' },
        { value: 'video', label: 'Video', count: video.length, icon: 'pi pi-video' },
        { value: 'musica', label: 'Música', count: musica.length, icon: 'pi pi-volume-up' },
      ];
    },

    filteredBrands() {
      let result = this.brandsWithLetter;

      // Filter by status
      if (this.statusFilter !== 'all') {
        result = result.filter((b) => {
          switch (this.statusFilter) {
            case 'conectados':
              return (b.conectados || 0) > 0;
            case 'desconectados':
              return !b.conectados || b.conectados === 0;
            case 'volumen_bajo':
              return (b.volumen_bajo || 0) > 0;
            case 'pausados':
              return (b.pausado || 0) > 0;
            default:
              return true;
          }
        });
      }

      // Filter by type (Sonara / Vybroo)
      if (this.typeFilter !== 'all') {
        if (this.typeFilter === 'sonara') {
          result = result.filter((b) => b._isSonara);
        } else if (this.typeFilter === 'vybroo') {
          result = result.filter((b) => !b._isSonara);
        }
      }

      // Filter by media (Video / Música)
      if (this.mediaFilter !== 'all') {
        if (this.mediaFilter === 'video') {
          result = result.filter((b) => b._isVideo);
        } else if (this.mediaFilter === 'musica') {
          result = result.filter((b) => !b._isVideo);
        }
      }

      // Filter by search query
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        result = result.filter((b) => {
          const name = (b.name || '').toLowerCase();
          const link = (b.link || '').toLowerCase();
          return name.includes(q) || link.includes(q);
        });
      }

      // Sort by letter, then by name for grouping
      result.sort((a, b) => {
        const lc = (a._letter || '').localeCompare(b._letter || '');
        if (lc !== 0) return lc;
        return (a.name || '').localeCompare(b.name || '');
      });


      return result;
    },

    availableLetters() {
      const letters = new Set();
      for (const b of this.filteredBrands) {
        if (b._letter) letters.add(b._letter);
      }
      return letters;
    },

    alphaLetters() {
      const letters = [];
      for (let i = 65; i <= 90; i++) {
        letters.push(String.fromCharCode(i));
      }
      letters.push('#');
      return letters;
    },

  },

  mounted() {
    this.loadBrands();
    this.$nextTick(() => {
      this.backToTopScrollTarget = this.resolveScrollTarget();
      this.updateBackToTopOffset();
      this.bindBackToTopScroll();
      this.handleBackToTopVisibility();
      window.addEventListener('resize', this.updateBackToTopOffset, { passive: true });
    });
  },

  beforeUnmount() {
    this.unbindBackToTopScroll();
    window.removeEventListener('resize', this.updateBackToTopOffset);
  },



  methods: {
    resolveScrollTarget() {
      let current = this.$el;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const hasScrollableOverflow = /(auto|scroll)/.test(style.overflowY || '');
        if (hasScrollableOverflow && current.scrollHeight > current.clientHeight) {
          return current;
        }
        current = current.parentElement;
      }
      return window;
    },

    bindBackToTopScroll() {
      const target = this.backToTopScrollTarget || window;
      target.addEventListener('scroll', this.handleBackToTopVisibility, { passive: true });
    },

    unbindBackToTopScroll() {
      const target = this.backToTopScrollTarget || window;
      target.removeEventListener('scroll', this.handleBackToTopVisibility);
    },

    updateBackToTopOffset() {
      const target = this.backToTopScrollTarget || window;
      const baseOffset = 12;
      const separation = 8;
      const scrollbarWidth = target === window
        ? Math.max(0, window.innerWidth - document.documentElement.clientWidth)
        : Math.max(0, target.offsetWidth - target.clientWidth);
      const offset = baseOffset + scrollbarWidth + separation;
      if (this.$el && this.$el.style) {
        this.$el.style.setProperty('--canales-fab-offset', `${offset}px`);
      }
    },

    handleBackToTopVisibility() {
      const target = this.backToTopScrollTarget || window;
      const top = target === window
        ? (window.scrollY || window.pageYOffset || 0)
        : target.scrollTop;
      this.showBackToTop = top > 80;
    },

    scrollToTop() {
      const target = this.backToTopScrollTarget || window;
      if (target === window) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      target.scrollTo({ top: 0, behavior: 'smooth' });
    },

    logoBackgroundStyle(brand) {
      const color = this.normalizeChannelColor(brand && brand.color);
      return { backgroundColor: color || '#3f3f46' };
    },

    normalizeChannelColor(rawColor) {
      const value = String(rawColor || '').trim();
      if (!value) return null;

      const hex = value.replace(/^#/, '').toLowerCase();
      if (/^[0-9a-f]{3}$/.test(hex) || /^[0-9a-f]{6}$/.test(hex)) {
        return `#${hex}`;
      }

      return null;
    },

    scrollToLetter(letter) {
      if (!letter) return;
      this.tableSortField = null;
      this.tableSortOrder = null;
      this.$nextTick(() => {
        const idx = this.filteredBrands.findIndex(b => b._letter === letter);
        if (idx < 0) return;
        const rows = this.$el.querySelectorAll('.p-datatable-tbody > tr');
        const target = rows[idx];
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    getLogoUrl(brand) {
      if (brand.mobileimage) return 'https://vybroo.net/images' + brand.mobileimage;
      return '';
    },

    getBrandLink(brand) {
      const link = brand && brand.link ? String(brand.link).trim() : '';
      if (!link) return '#';
      if (this.isVideoEnabled(brand)) return 'https://vybroo.app/' + link;
      if (brand.sonara) return 'http://sonara.red/' + link;
      return 'http://app.vybroo.net/' + link;
    },

    isVideoEnabled(brand) {
      if (!brand || typeof brand !== 'object') return false;
      const flags = [
        brand.is_video, brand.isVideo, brand.video,
        brand.video_enabled, brand.videoEnabled,
        brand.has_video, brand.hasVideo, brand.tiene_video,
        brand.id_video_youtube,
      ];
      for (const f of flags) {
        if (this.isTruthy(f)) return true;
      }
      const videoRe = /video/i;
      for (const field of [brand.stream, brand.link, brand.name]) {
        if (typeof field === 'string' && videoRe.test(field)) return true;
      }
      return false;
    },

    isTruthy(value) {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value > 0;
      if (typeof value === 'string') {
        const n = value.trim().toLowerCase();
        return n !== '' && n !== '0' && n !== 'false' && n !== 'no';
      }
      return false;
    },

    onSort(event) {
      this.tableSortField = event.sortField || null;
      this.tableSortOrder = typeof event.sortOrder !== 'undefined' ? event.sortOrder : null;
    },

    whatsAppBrand(brand) {
      const url = this.getBrandLink(brand);
      const text = `Aqu&iacute; puedes entrar al radio de ${brand.name || ''}: ${url}`;
      window.open(
        `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
        '_blank',
        'noopener'
      );
    },

    async copyLink(url) {
      try {
        await navigator.clipboard.writeText(url);
        this.$toast.add({ severity: 'success', summary: 'Link copiado', detail: url, group: 'canales', life: 2000 });
      } catch (e) {
        const el = document.createElement('textarea');
        el.value = url;
        el.style.position = 'absolute';
        el.style.left = '-90000px';
        document.body.appendChild(el);
        el.select();
        try {
          document.execCommand('copy');
          this.$toast.add({ severity: 'success', summary: 'Link copiado', detail: url, group: 'canales', life: 2000 });
        } catch (e2) {
          console.error('Error copying link:', e2);
        }
        document.body.removeChild(el);
      }
    },


    async loadBrands() {

      this.loading = true;
      this.$emit('update-loading', true);

      const token = localStorage.getItem('token');
      if (!token) {
        this.error = 'Sesi&oacute;n no encontrada. Inicia sesi&oacute;n nuevamente.';
        this.loading = false;
        this.$emit('update-loading', false);
        return;
      }

      try {
        const response = await axiosInstance.post('/api/connectionmonitor', { token });
        this.brands = response.data && response.data.data ? response.data.data : [];
        this.error = '';
        this.loading = false;
        this.$emit('update-loading', false);
      } catch (err) {
        console.error('Error loading brands:', err);
        this.error = 'Error al cargar canales.';
        this.loading = false;
        this.$emit('update-loading', false);
      }
    },
  },
};
</script>

<style>
@import "@/assets/media-module-base.css";

/* === Hero diferenciado === */
.page-canales {
  --media-hero-bg: linear-gradient(135deg, #eff6ff 0%, #f0f4ff 100%);
  --media-hero-border: rgba(147, 197, 253, 0.4);
  --media-hero-bg-dark: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(24, 24, 27, 0.98) 100%);
  --media-hero-border-dark: rgba(96, 165, 250, 0.2);
}

/* === Toolbar === */
.canales-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.canales-toolbar-search {
  position: relative;
  flex: 0 0 320px;
  min-width: 240px;
  max-width: 360px;
}

.canales-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.76rem;
  color: #94a3b8;
  pointer-events: none;
  z-index: 1;
}

.canales-filter-input.p-inputtext {
  width: 100%;
  height: 42px;
  border-radius: 10px;
  padding-left: 2rem;
}

.canales-toolbar-actions {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

/* === Status filter chips === */
.canales-status-filters {
  display: flex;
  align-items: center;
}

.canales-filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.canales-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #475569;
  background: rgba(255, 255, 255, 0.94);
  cursor: pointer;
  user-select: none;
  transition: border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.canales-filter-chip input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.canales-filter-chip:hover {
  transform: translateY(-1px);
}

.canales-filter-chip.is-active {
  box-shadow: inset 0 0 0 1px rgba(147, 197, 253, 0.4);
}

.canales-filter-count {
  font-size: 0.65rem;
  font-weight: 700;
  opacity: 0.6;
  min-width: 14px;
  text-align: center;
}

/* === Chip colors by active state (position-based) === */
.canales-filter-chip:nth-of-type(1).is-active {
  border-color: rgba(59, 130, 246, 0.6);
  background: rgba(219, 234, 254, 0.9);
  color: #1d4ed8;
}

.canales-filter-chip:nth-of-type(2).is-active {
  border-color: rgba(34, 197, 94, 0.6);
  background: rgba(220, 252, 231, 0.95);
  color: #15803d;
}

.canales-filter-chip:nth-of-type(3).is-active {
  border-color: rgba(239, 68, 68, 0.55);
  background: rgba(254, 226, 226, 0.95);
  color: #b91c1c;
}

.canales-filter-chip:nth-of-type(4).is-active {
  border-color: rgba(251, 146, 60, 0.6);
  background: rgba(255, 237, 213, 0.95);
  color: #c2410c;
}

.canales-filter-chip:nth-of-type(5).is-active {
  border-color: rgba(239, 68, 68, 0.6);
  background: rgba(254, 226, 226, 0.95);
  color: #b91c1c;
}

/* Dark mode chips */
.dark .canales-filter-chip {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #d4d4d8;
}

.dark .canales-filter-chip:hover {
  border-color: rgba(96, 165, 250, 0.55);
  background: rgba(30, 41, 59, 0.85);
  color: #bfdbfe;
}

.dark .canales-filter-chip:nth-of-type(1).is-active {
  border-color: rgba(96, 165, 250, 0.75);
  background: rgba(30, 58, 138, 0.3);
  color: #dbeafe;
}

.dark .canales-filter-chip:nth-of-type(2).is-active {
  border-color: rgba(74, 222, 128, 0.75);
  background: rgba(20, 83, 45, 0.28);
  color: #dcfce7;
}

.dark .canales-filter-chip:nth-of-type(3).is-active {
  border-color: rgba(248, 113, 113, 0.7);
  background: rgba(127, 29, 29, 0.28);
  color: #fecaca;
}

.dark .canales-filter-chip:nth-of-type(4).is-active {
  border-color: rgba(251, 146, 60, 0.7);
  background: rgba(124, 45, 18, 0.28);
  color: #fed7aa;
}

.dark .canales-filter-chip:nth-of-type(5).is-active {
  border-color: rgba(248, 113, 113, 0.7);
  background: rgba(127, 29, 29, 0.28);
  color: #fecaca;
}

/* === Stat columns: subtle icon + number === */
.canales-stat-col {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.canales-stat-green {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #16a34a;
}

.canales-stat-orange {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #ea580c;
}

.canales-stat-orange.is-zero {
  color: #d4d4d8;
  opacity: 0.5;
}

.canales-stat-red {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #dc2626;
}

.canales-stat-red.is-zero {
  color: #d4d4d8;
  opacity: 0.5;
}

.dark .canales-stat-green {
  color: #4ade80;
}

.dark .canales-stat-orange {
  color: #fb923c;
}

.dark .canales-stat-orange.is-zero {
  color: #52525b;
  opacity: 0.4;
}

.dark .canales-stat-red {
  color: #f87171;
}

.dark .canales-stat-red.is-zero {
  color: #52525b;
  opacity: 0.4;
}

/* === Platform / Media type badges === */
.canales-badge-type {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.4;
  white-space: nowrap;
}



.canales-badge-type.badge-video {
  background: rgba(254, 249, 195, 0.9);
  color: #a16207;
}

.canales-badge-type.badge-musica {
  background: rgba(243, 232, 255, 0.9);
  color: #7c3aed;
}



.dark .canales-badge-type.badge-video {
  background: rgba(113, 63, 18, 0.3);
  color: #fde68a;
}

.dark .canales-badge-type.badge-musica {
  background: rgba(91, 33, 182, 0.25);
  color: #d8b4fe;
}

/* === Secondary filters row (Tipo & Media) === */
.canales-secondary-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.canales-filter-separator {
  width: 1px;
  height: 24px;
  background: rgba(148, 163, 184, 0.3);
  flex-shrink: 0;
}

.dark .canales-filter-separator {
  background: rgba(113, 113, 122, 0.3);
}

/* === A-Z Navigation === */
.canales-table-wrapper {
  display: flex;
  gap: 0;
  position: relative;
}

.canales-table-wrapper .media-table-wrap {
  flex: 1;
  min-width: 0;
}

.canales-alpha-nav {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 6px 3px;
  margin-left: 6px;
  position: sticky;
  top: 0;
  align-self: flex-start;
  z-index: 10;
  background: rgba(248, 250, 252, 0.85);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  max-height: 540px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.canales-alpha-nav::-webkit-scrollbar {
  display: none;
}

.canales-alpha-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 700;
  cursor: default;
  transition: all 0.12s ease;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;
}

.canales-alpha-btn.is-available {
  color: #334155;
  cursor: pointer;
}

.canales-alpha-btn.is-empty {
  color: #cbd5e1;
  opacity: 0.4;
}

.canales-alpha-btn.is-available:hover {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
  transform: scale(1.15);
}

.canales-alpha-btn.is-available:active {
  transform: scale(0.92);
}

.dark .canales-alpha-nav {
  background: rgba(24, 24, 27, 0.88);
  border-color: rgba(113, 113, 122, 0.25);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.dark .canales-alpha-btn {
  color: #71717a;
}

.dark .canales-alpha-btn.is-available {
  color: #d4d4d8;
}

.dark .canales-alpha-btn.is-empty {
  color: #3f3f46;
  opacity: 0.35;
}

.dark .canales-alpha-btn.is-available:hover {
  background: rgba(96, 165, 250, 0.15);
  color: #93c5fd;
}

/* === Table column widths stable === */
.canales-table .p-datatable-thead > tr > th,
.canales-table .p-datatable-tbody > tr > td {
  box-sizing: border-box;
}

/* === Responsive === */
@media (max-width: 1024px) {
  .canales-toolbar-search {
    flex: 1 1 100%;
    max-width: none;
  }

  .canales-status-filters {
    width: 100%;
  }

  .canales-filter-group {
    width: 100%;
  }

  .canales-alpha-nav {
    padding: 5px 2px;
    margin-left: 4px;
  }

  .canales-alpha-btn {
    width: 20px;
    height: 18px;
    font-size: 0.58rem;
  }
}

@media (max-width: 768px) {
  .canales-alpha-nav {
    display: none;
  }
}

/* === Botones de acci&oacute;n m&aacute;s compactos === */
.canales-toolbar-actions .p-button {
  width: 36px;
  height: 36px;
}

/* === Floating Top Button === */
.canales-top-fab {
  --fab-offset: var(--canales-fab-offset, 20px);
  position: fixed;
  right: var(--fab-offset);
  bottom: var(--fab-offset);
  z-index: 1200;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  min-height: 40px !important;
  max-width: 40px;
  max-height: 40px;
  aspect-ratio: 1 / 1 !important;
  box-sizing: border-box;
  line-height: 1;
  flex: 0 0 40px;
  padding: 0;
  border: 1px solid rgba(147, 197, 253, 0.65);
  border-radius: 9999px !important;
  background: linear-gradient(135deg, rgba(239, 246, 255, 0.96) 0%, rgba(224, 242, 254, 0.95) 100%);
  color: #1d4ed8;
  display: inline-grid;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba(30, 64, 175, 0.18);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.canales-top-fab:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(30, 64, 175, 0.24);
  border-color: rgba(59, 130, 246, 0.8);
}

.canales-top-fab:active {
  transform: translateY(0);
}

.canales-top-fab i {
  font-size: 0.86rem;
}

.dark .canales-top-fab {
  border-color: rgba(96, 165, 250, 0.45);
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 41, 59, 0.9) 100%);
  color: #bfdbfe;
  box-shadow: 0 12px 28px rgba(2, 6, 23, 0.5);
}

.dark .canales-top-fab:hover {
  border-color: rgba(147, 197, 253, 0.65);
  box-shadow: 0 16px 32px rgba(2, 6, 23, 0.6);
}

.canales-top-fab-enter-active,
.canales-top-fab-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.canales-top-fab-enter-from,
.canales-top-fab-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 768px) {
  .canales-top-fab {
    width: 38px !important;
    height: 38px !important;
    min-width: 38px !important;
    min-height: 38px !important;
    max-width: 38px;
    max-height: 38px;
    flex-basis: 38px;
  }
}
</style>
